import { createContext, useContext, useState, ReactNode } from 'react'

export type VehicleFilter = 'alle' | 'personbil' | 'varebil'

interface VehicleFilterContextType {
  vehicleFilter: VehicleFilter
  setVehicleFilter: (filter: VehicleFilter) => void
  getVarebilParam: () => boolean | undefined
}

// Default context: 'personbil' standalone (works in tests without a provider).
const VehicleFilterContext = createContext<VehicleFilterContextType | undefined>(undefined)

const DEFAULT_CONTEXT: VehicleFilterContextType = {
  vehicleFilter: 'personbil',
  setVehicleFilter: () => {},
  getVarebilParam: () => false,
}

export function VehicleFilterProvider({ children }: { children: ReactNode }) {
  // Default to 'personbil' to maintain current behavior
  const [vehicleFilter, setVehicleFilter] = useState<VehicleFilter>('personbil')

  // Returns the correct varebil parameter value for API calls
  const getVarebilParam = (): boolean | undefined => {
    switch (vehicleFilter) {
      case 'personbil':
        return false
      case 'varebil':
        return true
      case 'alle':
      default:
        return undefined // Omit parameter for 'alle'
    }
  }

  return (
    <VehicleFilterContext.Provider value={{ vehicleFilter, setVehicleFilter, getVarebilParam }}>
      {children}
    </VehicleFilterContext.Provider>
  )
}

export function useVehicleFilter() {
  const context = useContext(VehicleFilterContext)
  return context ?? DEFAULT_CONTEXT
}

// Helper function to build API params with the varebil filter
export function buildVehicleParams(
  varebilParam: boolean | undefined,
  otherParams: Record<string, any>
): Record<string, any> {
  const params = { ...otherParams }
  
  if (varebilParam !== undefined) {
    params.varebil = varebilParam
  }
  // For 'alle' (undefined) - don't add varebil param at all
  
  return params
}
